/**
 * Photo-based Try-On (live 3D garment object over an uploaded photo)
 *
 * The customer uploads one photo. MediaPipe's free, in-browser pose
 * detector finds their shoulders once, and the garment is built as a real
 * 3D object (front + back curved, lit panels — actual geometry, not a
 * baked flat image) in a live Three.js scene layered on top of the photo.
 * The customer can freely drag it in X/Y, pinch or scroll to resize it,
 * and use the Turn / Depth sliders to rotate it and move it forward/back
 * through space — nothing here constrains or collides it with anything.
 *
 * What this does NOT do: simulate cloth physics or drape, or fit the
 * customer's actual 3D body shape. It's a real 3D object placed and sized
 * from 2D pose landmarks — closer to real than a flat cutout, with an
 * intentional margin of error rather than a body scan.
 *
 * Dependencies: @mediapipe/tasks-vision and three.js, both loaded from a
 * CDN at runtime (free, no API key, no server-side cost).
 */
(function () {
  'use strict';

  var MEDIAPIPE_CDN =
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs';
  var POSE_MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
  var THREE_CDN = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';
  var MAX_DIMENSION = 1200; // downscale huge photos for performance

  document.querySelectorAll('[data-virtual-try-on]').forEach(function (root) {
    // Guarantee correct placement regardless of where the {% render %} tag
    // landed in the theme template: physically move our button to sit right
    // after the real quantity stepper element.
    var qtyInput = document.querySelector('quantity-input.quantity, quantity-input');
    if (qtyInput && qtyInput.parentNode) {
      qtyInput.insertAdjacentElement('afterend', root);
    }
  });

  document.querySelectorAll('[data-virtual-try-on]').forEach(initTryOn);

  function initTryOn(root) {
    var toggleBtn = root.querySelector('[data-vto-toggle]');
    var modal = root.querySelector('[data-vto-modal]');
    var unavailableNote = root.querySelector('[data-vto-unavailable]');

    if (!modal) {
      if (toggleBtn && unavailableNote) {
        toggleBtn.addEventListener('click', function () {
          unavailableNote.hidden = !unavailableNote.hidden;
        });
      }
      return;
    }

    var backdrop = modal.querySelector('[data-vto-modal-backdrop]');
    var closeBtn = modal.querySelector('[data-vto-modal-close]');
    var stageCancelBtn = modal.querySelector('[data-vto-stage-cancel]');
    var uploadStage = modal.querySelector('[data-vto-upload-stage]');
    var resultStage = modal.querySelector('[data-vto-result-stage]');
    var photoCanvas = modal.querySelector('[data-vto-photo-canvas]');
    var glCanvas = modal.querySelector('[data-vto-gl-canvas]');
    var statusEl = modal.querySelector('[data-vto-ar-status]');
    var fileInput = modal.querySelector('[data-vto-file-input]');
    var fileInput2 = modal.querySelector('[data-vto-file-input-2]');
    var resetBtn = modal.querySelector('[data-vto-ar-reset]');
    var downloadLink = modal.querySelector('[data-vto-ar-download]');
    var resultControls = modal.querySelector('[data-vto-result-controls]');
    var resultControls2 = modal.querySelector('[data-vto-result-controls-2]');
    var slidersEl = modal.querySelector('[data-vto-sliders]');
    var rotateSlider = modal.querySelector('[data-vto-rotate-slider]');
    var depthSlider = modal.querySelector('[data-vto-depth-slider]');
    var hintEl = modal.querySelector('[data-vto-ar-hint]');
    var garmentMapEl = modal.querySelector('[data-vto-garment-map]');

    var garmentMap = {};
    try {
      garmentMap = JSON.parse(garmentMapEl.textContent);
    } catch (e) {
      console.error('Virtual Try-On: could not parse garment map JSON', e);
      return;
    }

    var photoCtx = photoCanvas.getContext('2d');
    var poseLandmarker = null;
    var threeModule = null;
    var THREE = null;
    var renderer = null;
    var scene = null;
    var camera = null;
    var garmentGroup = null;
    var garmentTexCache = {}; // garment URL pair -> { frontTexture, backTexture }
    var photoImg = null;
    var currentColorKey = '';
    var base = null; // { x, y, width, height, angleDeg }
    var manual = { offsetX: 0, offsetY: 0, scale: 1, rotateDeg: 0, depth: 0 };

    toggleBtn.addEventListener('click', openModal);
    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
    stageCancelBtn.addEventListener('click', closeModal);
    fileInput.addEventListener('change', handleFileChosen);
    fileInput2.addEventListener('change', handleFileChosen);
    resetBtn.addEventListener('click', function () {
      manual.offsetX = 0;
      manual.offsetY = 0;
      manual.scale = 1;
      manual.rotateDeg = 0;
      manual.depth = 0;
      rotateSlider.value = 0;
      depthSlider.value = 0;
      base = null;
      if (photoImg) fitGarment();
      applyTransform();
      renderScene();
    });
    rotateSlider.addEventListener('input', function () {
      manual.rotateDeg = parseFloat(rotateSlider.value) || 0;
      applyTransform();
      renderScene();
    });
    depthSlider.addEventListener('input', function () {
      manual.depth = parseFloat(depthSlider.value) || 0;
      applyTransform();
      renderScene();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    setupManualControls();

    function openModal() {
      modal.hidden = false;
      showUploadStage();
    }

    function closeModal() {
      modal.hidden = true;
    }

    function showUploadStage() {
      uploadStage.hidden = false;
      resultStage.hidden = true;
      resultControls.hidden = true;
      resultControls2.hidden = true;
      slidersEl.hidden = true;
      hintEl.hidden = true;
    }

    async function handleFileChosen(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;

      uploadStage.hidden = true;
      resultStage.hidden = false;
      setStatus('Loading photo…');

      var objectUrl = URL.createObjectURL(file);
      var img = new Image();
      img.onload = async function () {
        photoImg = img;
        setupCanvasesForPhoto();
        setStatus('Finding your shoulders…');

        try {
          await ensurePoseLandmarker();
          fitGarment();
        } catch (err) {
          console.error('Virtual Try-On: pose detection failed', err);
          setStatus('Could not detect a pose in this photo — try a clearer, front-facing photo.');
          return;
        }

        setStatus('Building the 3D shirt…');
        var loadResult;
        try {
          await ensureScene();
          loadResult = await loadGarmentForCurrentSelection();
        } catch (err) {
          console.error('Virtual Try-On: 3D setup failed', err);
          setStatus('Could not set up the 3D view. Please try again.');
          return;
        }

        manual.offsetX = 0;
        manual.offsetY = 0;
        manual.scale = 1;
        manual.rotateDeg = 0;
        manual.depth = 0;
        rotateSlider.value = 0;
        depthSlider.value = 0;

        applyTransform();
        renderScene();

        if (loadResult && loadResult.isFallback) {
          setStatus(
            'Your selected color isn\'t available for try-on yet — showing it in ' +
              loadResult.fallbackColor +
              ' instead.'
          );
        } else {
          setStatus('');
        }
        resultControls.hidden = false;
        resultControls2.hidden = false;
        slidersEl.hidden = false;
        hintEl.hidden = false;
      };
      img.onerror = function () {
        setStatus('Could not load that photo. Please try a different file.');
      };
      img.src = objectUrl;
    }

    function setupCanvasesForPhoto() {
      var w = photoImg.naturalWidth;
      var h = photoImg.naturalHeight;
      var scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
      var cw = Math.round(w * scale);
      var ch = Math.round(h * scale);

      photoCanvas.width = cw;
      photoCanvas.height = ch;
      glCanvas.width = cw;
      glCanvas.height = ch;

      photoCtx.drawImage(photoImg, 0, 0, cw, ch);

      if (camera) {
        camera.aspect = cw / ch;
        var fovRad = (camera.fov * Math.PI) / 180;
        camera.position.set(cw / 2, ch / 2, ch / 2 / Math.tan(fovRad / 2));
        camera.lookAt(cw / 2, ch / 2, 0);
        camera.updateProjectionMatrix();
      }
      if (renderer) {
        renderer.setSize(cw, ch, false);
      }
    }

    async function ensurePoseLandmarker() {
      if (poseLandmarker) return;
      var vision = await import(MEDIAPIPE_CDN);
      var filesetResolver = await vision.FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
      );
      poseLandmarker = await vision.PoseLandmarker.createFromOptions(filesetResolver, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'GPU' },
        runningMode: 'IMAGE',
        numPoses: 1
      });
    }

    function fitGarment() {
      var result = poseLandmarker.detect(photoCanvas);
      if (!result.landmarks || result.landmarks.length === 0) {
        throw new Error('No pose landmarks detected');
      }
      var landmarks = result.landmarks[0];

      var lShoulder = landmarks[11]; // anatomical left shoulder
      var rShoulder = landmarks[12]; // anatomical right shoulder
      if (!lShoulder || !rShoulder) throw new Error('Shoulders not detected');

      var toPx = function (pt) {
        return { x: pt.x * photoCanvas.width, y: pt.y * photoCanvas.height };
      };

      // Photos aren't mirrored like a selfie camera, so the anatomical LEFT
      // shoulder appears on the RIGHT side of the image, and vice versa.
      var ls = toPx(rShoulder); // screen-left point
      var rs = toPx(lShoulder); // screen-right point

      var shoulderMid = { x: (ls.x + rs.x) / 2, y: (ls.y + rs.y) / 2 };
      var shoulderWidthPx = Math.hypot(rs.x - ls.x, rs.y - ls.y);

      var lHip = landmarks[23];
      var rHip = landmarks[24];
      var hipsVisible =
        lHip && rHip && (lHip.visibility === undefined || (lHip.visibility > 0.5 && rHip.visibility > 0.5));
      var torsoHeightPx;
      if (hipsVisible) {
        var lh = toPx(lHip);
        var rh = toPx(rHip);
        var hipMid = { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2 };
        torsoHeightPx = Math.hypot(hipMid.x - shoulderMid.x, hipMid.y - shoulderMid.y);
      } else {
        torsoHeightPx = shoulderWidthPx * 1.35;
      }

      base = {
        width: shoulderWidthPx * 2.1,
        height: torsoHeightPx * 2.4,
        x: shoulderMid.x,
        y: shoulderMid.y - torsoHeightPx * 0.15,
        angleDeg: (Math.atan2(rs.y - ls.y, rs.x - ls.x) * 180) / Math.PI
      };
    }

    async function ensureThree() {
      if (threeModule) return threeModule;
      threeModule = await import(THREE_CDN);
      THREE = threeModule;
      return threeModule;
    }

    async function ensureScene() {
      await ensureThree();
      if (renderer) return; // already set up

      renderer = new THREE.WebGLRenderer({ canvas: glCanvas, alpha: true, antialias: true });
      renderer.setPixelRatio(1);
      renderer.setSize(photoCanvas.width, photoCanvas.height, false);
      renderer.setClearColor(0x000000, 0); // fully transparent — some browsers default an
      // untouched WebGL canvas to opaque black otherwise, which was hiding the photo beneath it

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(35, photoCanvas.width / photoCanvas.height, 1, 5000);
      var fovRad = (camera.fov * Math.PI) / 180;
      camera.position.set(
        photoCanvas.width / 2,
        photoCanvas.height / 2,
        photoCanvas.height / 2 / Math.tan(fovRad / 2)
      );
      camera.lookAt(photoCanvas.width / 2, photoCanvas.height / 2, 0);

      scene.add(new THREE.AmbientLight(0xffffff, 0.65));
      var key = new THREE.DirectionalLight(0xffffff, 0.85);
      key.position.set(-200, 300, 400);
      scene.add(key);
      var fill = new THREE.DirectionalLight(0xffffff, 0.3);
      fill.position.set(200, -100, 300);
      scene.add(fill);

      renderer.render(scene, camera); // explicit transparent first frame, before the garment exists
    }

    function makeCurvedPanelGeometry(w, h, bulge) {
      var segments = 30;
      var geometry = new THREE.PlaneGeometry(w, h, segments, segments);
      var pos = geometry.attributes.position;
      for (var i = 0; i < pos.count; i++) {
        var x = pos.getX(i) / (w / 2);
        var y = pos.getY(i) / (h / 2);
        var falloff = Math.max(0, Math.cos((x * Math.PI) / 2) * Math.cos((y * Math.PI) / 2));
        pos.setZ(i, bulge * Math.pow(falloff, 1.3));
      }
      geometry.computeVertexNormals();
      return geometry;
    }

    function textureFromImage(img) {
      var tex = new THREE.Texture(img);
      tex.needsUpdate = true;
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    }

    async function loadImage(src) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          resolve(img);
        };
        img.onerror = reject;
        img.src = src;
      });
    }

    async function loadGarmentForCurrentSelection() {
      var color = getSelectedColorLabel() || Object.keys(garmentMap)[0];
      var entry = garmentMap[color];
      var isFallback = false;
      var fallbackColor = null;
      if (!entry) {
        fallbackColor = Object.keys(garmentMap)[0];
        entry = garmentMap[fallbackColor];
        isFallback = true;
      }
      if (!entry || !entry.front) return { isFallback: false, fallbackColor: null };

      var cacheKey = entry.front + '|' + (entry.back || '');
      if (cacheKey === currentColorKey && garmentGroup) return { isFallback: isFallback, fallbackColor: fallbackColor };

      var cached = garmentTexCache[cacheKey];
      var frontImg, backImg;
      if (cached) {
        frontImg = cached.frontImg;
        backImg = cached.backImg;
      } else {
        frontImg = await loadImage(entry.front);
        backImg = entry.back ? await loadImage(entry.back) : frontImg;
        garmentTexCache[cacheKey] = { frontImg: frontImg, backImg: backImg };
      }

      if (garmentGroup) {
        scene.remove(garmentGroup);
        disposeGroup(garmentGroup);
      }

      var w = base ? base.width : 400;
      var h = base ? base.height : 500;
      var depthGap = w * 0.12;

      var frontGeo = makeCurvedPanelGeometry(w, h, w * 0.12);
      var frontMat = new THREE.MeshStandardMaterial({
        map: textureFromImage(frontImg),
        transparent: true,
        alphaTest: 0.05,
        roughness: 0.9,
        side: THREE.DoubleSide
      });
      var frontMesh = new THREE.Mesh(frontGeo, frontMat);
      frontMesh.position.z = depthGap / 2;

      var backGeo = makeCurvedPanelGeometry(w * 0.96, h * 0.98, w * 0.08);
      var backMat = new THREE.MeshStandardMaterial({
        map: textureFromImage(backImg),
        transparent: true,
        alphaTest: 0.05,
        roughness: 0.9,
        side: THREE.DoubleSide
      });
      var backMesh = new THREE.Mesh(backGeo, backMat);
      backMesh.position.z = -depthGap / 2;
      backMesh.rotation.y = Math.PI; // face outward toward the back

      garmentGroup = new THREE.Group();
      garmentGroup.add(frontMesh);
      garmentGroup.add(backMesh);
      scene.add(garmentGroup);

      currentColorKey = cacheKey;
      return { isFallback: isFallback, fallbackColor: fallbackColor };
    }

    function applyTransform() {
      if (!garmentGroup || !base) return;
      garmentGroup.position.set(base.x + manual.offsetX, base.y + manual.offsetY, manual.depth);
      garmentGroup.rotation.z = (base.angleDeg * Math.PI) / 180;
      garmentGroup.rotation.y = (manual.rotateDeg * Math.PI) / 180;
      garmentGroup.scale.setScalar(manual.scale);
    }

    function renderScene() {
      if (renderer && scene && camera) {
        renderer.render(scene, camera);
      }
      buildDownload();
    }

    function buildDownload() {
      var out = document.createElement('canvas');
      out.width = photoCanvas.width;
      out.height = photoCanvas.height;
      var octx = out.getContext('2d');
      octx.drawImage(photoCanvas, 0, 0);
      octx.drawImage(glCanvas, 0, 0);
      downloadLink.href = out.toDataURL('image/png');
    }

    function disposeGroup(group) {
      group.traverse(function (obj) {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (obj.material.map) obj.material.map.dispose();
          obj.material.dispose();
        }
      });
    }

    // --- Manual drag-to-move (X/Y) + pinch/wheel-to-zoom ---
    function setupManualControls() {
      var pointers = {};
      var dragging = false;
      var lastX = 0;
      var lastY = 0;
      var pinchStartDist = 0;
      var pinchStartScale = 1;
      var pinchStartMidY = 0;
      var pinchStartDepth = 0;

      function canvasScale() {
        var rect = glCanvas.getBoundingClientRect();
        return { x: glCanvas.width / rect.width, y: glCanvas.height / rect.height };
      }

      glCanvas.style.touchAction = 'none';

      glCanvas.addEventListener('pointerdown', function (e) {
        if (!base) return;
        glCanvas.setPointerCapture(e.pointerId);
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(pointers);
        if (ids.length === 1) {
          dragging = true;
          lastX = e.clientX;
          lastY = e.clientY;
        } else if (ids.length === 2) {
          dragging = false;
          pinchStartDist = twoPointerDistance();
          pinchStartScale = manual.scale;
          pinchStartMidY = twoPointerMidY();
          pinchStartDepth = manual.depth;
        }
      });

      glCanvas.addEventListener('pointermove', function (e) {
        if (!(e.pointerId in pointers)) return;
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(pointers);
        var s = canvasScale();
        var changed = false;

        if (ids.length === 1 && dragging) {
          manual.offsetX += (e.clientX - lastX) * s.x;
          // Y is inverted here on purpose: the 3D scene treats "up" the
          // opposite way screen pixels do, so without this flip, dragging
          // down moved the shirt up and vice versa.
          manual.offsetY -= (e.clientY - lastY) * s.y;
          lastX = e.clientX;
          lastY = e.clientY;
          changed = true;
        } else if (ids.length === 2) {
          var dist = twoPointerDistance();
          if (pinchStartDist > 0) {
            var ratio = dist / pinchStartDist;
            manual.scale = clamp(pinchStartScale * ratio, 0.3, 3);
            changed = true;
          }
          // Two-finger drag up/down moves the shirt through depth (Z) —
          // dragging the pair up brings it closer, down pushes it back.
          var midY = twoPointerMidY();
          var depthDelta = (pinchStartMidY - midY) * s.y;
          manual.depth = clamp(pinchStartDepth + depthDelta, -400, 400);
          depthSlider.value = Math.round(manual.depth);
          changed = true;
        }
        if (changed) {
          applyTransform();
          renderScene();
        }
      });

      function endPointer(e) {
        delete pointers[e.pointerId];
        var ids = Object.keys(pointers);
        dragging = ids.length === 1;
        if (ids.length === 1) {
          lastX = pointers[ids[0]].x;
          lastY = pointers[ids[0]].y;
        }
      }
      glCanvas.addEventListener('pointerup', endPointer);
      glCanvas.addEventListener('pointercancel', endPointer);

      glCanvas.addEventListener(
        'wheel',
        function (e) {
          if (!base) return;
          e.preventDefault();
          var delta = e.deltaY > 0 ? -0.05 : 0.05;
          manual.scale = clamp(manual.scale + delta, 0.3, 3);
          applyTransform();
          renderScene();
        },
        { passive: false }
      );

      function twoPointerDistance() {
        var ids = Object.keys(pointers);
        if (ids.length < 2) return 0;
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        return Math.hypot(a.x - b.x, a.y - b.y);
      }

      function twoPointerMidY() {
        var ids = Object.keys(pointers);
        if (ids.length < 2) return 0;
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        return (a.y + b.y) / 2;
      }
    }

    function clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    }

    function setStatus(msg) {
      statusEl.textContent = msg;
      statusEl.hidden = !msg;
    }

    function getSelectedColorLabel() {
      var checkedRadio = document.querySelector(
        'input[type="radio"][name*="color" i]:checked, input[type="radio"][id*="color" i]:checked'
      );
      if (checkedRadio) return checkedRadio.value;

      var select = document.querySelector('select[name*="color" i], select[id*="color" i]');
      if (select && select.value) return select.value;

      return null;
    }
  }
})();
